import { TMeetingRoom } from "apis/meetings/meeting-room";
import React, { useEffect } from "react";
import { Container, ValueContainer } from "./MeetingRoomPolicyItem.styles";
import UI from "@mile-meeting/mile-ui";
import { SettingField } from "../types";
import { MeetingRoomHorizontalNonModal } from "uis/non-modal";
import { drop, get } from "lodash";

type MeetingRoomPolicyItemProps = {
  meetingRoom: TMeetingRoom;
  settingData?: TMeetingRoomSettingForPolicyItem[];
  parentModalRef: React.RefObject<HTMLDivElement>;
  itemListRef?: React.RefObject<HTMLDivElement>;
  itemIndex: number;
};

export type TMeetingRoomSettingForPolicyItem = {
  settingField: SettingField;
  value: string | number | boolean;
  onHandleChange?: (value: string | number | boolean) => void;
  disabled?: boolean;
};

const MeetingRoomPolicyItem: React.FC<MeetingRoomPolicyItemProps> = ({
  meetingRoom,
  settingData = [],
  parentModalRef,
  itemListRef,
  itemIndex,
}) => {
  const [hoverDetailInfo, setHoverDetailInfo] = React.useState<boolean>(false);
  const meetingRoomItemRef = React.useRef<HTMLDivElement>(null);
  const selectBoxRef = React.useRef<HTMLDivElement>(null);

  //* 회의실 상세 정보 모달 위치
  const getMeetingRoomDetailModalPosition = () => {
    const rect = meetingRoomItemRef.current?.getBoundingClientRect();
    const modalPosition = parentModalRef.current?.getBoundingClientRect();

    if ((rect?.bottom ?? 0) + 250 > (modalPosition?.bottom ?? 0)) {
      return { bottom: (modalPosition?.bottom ?? 0) - (rect?.top ?? 0) + 22, left: 76 };
    }

    return { top: (rect?.top ?? 0) - (modalPosition?.top ?? 0) + 44, left: 76 };
  };
  useEffect(() => {
    const scrollEl = itemListRef?.current;
    if (!scrollEl) return;

    const updateDropdownPosition = () => {
      const triggerEl = selectBoxRef.current;
      const containerRect = itemListRef?.current?.getBoundingClientRect();
      const triggerRect = triggerEl?.getBoundingClientRect();
      const modalRect = parentModalRef.current?.getBoundingClientRect();

      const dropdownEl = triggerEl?.parentElement?.querySelector(".select-items:not(.close)") as HTMLElement | null;

      if (!dropdownEl || !triggerRect || !containerRect || !modalRect) return;

      const marginTop = containerRect.top - modalRect.top;

      const top = triggerRect.bottom - containerRect.top + marginTop + 20;
      const left = triggerRect.left - modalRect.left + 20;
      const width = triggerRect.width;

      dropdownEl.style.position = "fixed";
      dropdownEl.style.top = `${top}px`;
      dropdownEl.style.left = `${left}px`;
      dropdownEl.style.width = `${width}px`;
    };

    const updateDropdownClose = () => {
      const triggerEl = selectBoxRef.current;
      const dropdownEl = triggerEl?.parentElement?.querySelector(".select-items:not(.close)") as HTMLElement | null;

      if (!dropdownEl) return;

      dropdownEl.classList.add("close");
    };

    const dropdown = selectBoxRef.current?.parentElement?.querySelector(".select-items") as HTMLElement | null;

    if (!dropdown) return;

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === "attributes") {
          const el = mutation.target as HTMLElement;
          const isOpen = !el.classList.contains("close");
          if (isOpen) {
            setTimeout(() => {
              updateDropdownPosition();
              scrollEl.addEventListener("scroll", updateDropdownClose);
            }, 0);
          } else {
            scrollEl.removeEventListener("scroll", updateDropdownClose);
          }
        }
      }
    });

    observer.observe(dropdown, { attributes: true });

    return () => {
      observer.disconnect();
      scrollEl?.removeEventListener("scroll", updateDropdownPosition);
    };
  }, []);

  return (
    <Container className="meeting-room-policy-item">
      <ValueContainer>
        <UI.Typography
          type="body01 normal"
          fontStyle="regular"
          color="var(--alias-color-label-normal)"
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            width: (630 - 32 - settingData.length * 40) / (settingData.length + 1),
          }}
        >
          {meetingRoom.name}
        </UI.Typography>
        <div
          style={{
            width: "max-content",
          }}
          onMouseEnter={() => setHoverDetailInfo(true)}
          onMouseLeave={() => setHoverDetailInfo(false)}
          ref={meetingRoomItemRef}
        >
          <UI.Typography
            type="label01"
            color="var(--alias-color-label-alternative)"
            style={{
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            상세 정보
          </UI.Typography>
          <MeetingRoomHorizontalNonModal
            positionFixed
            meetingRoom={meetingRoom}
            open={hoverDetailInfo}
            position={getMeetingRoomDetailModalPosition()}
          />
        </div>
      </ValueContainer>
      {settingData?.map((setting) => {
        const { settingField, value, onHandleChange, disabled } = setting;
        return (
          <ValueContainer>
            {settingField.type === "toggle" && (
              <UI.Selection.Toggle.Default state={value as boolean} onChange={onHandleChange} disabled={disabled} />
            )}
            {settingField.type === "select" && (
              <div ref={selectBoxRef}>
                <UI.SelectBox
                  disabled={disabled}
                  width={"100%"}
                  dropdownMaxHeight={114}
                  data={settingField.options.map((option) => `${option}분`)}
                  selectedData={[`${value.toString().split("분")[0]}분`]}
                  onChange={(value) => onHandleChange?.(parseInt(value[0].split("분")[0]))}
                  size="small"
                />
              </div>
            )}
          </ValueContainer>
        );
      })}
    </Container>
  );
};

export default React.memo(MeetingRoomPolicyItem);
